import {Component, EventEmitter, Input, OnDestroy,
	Output, ViewChild}                  from '@angular/core';
import {NgbTypeaheadSelectItemEvent}    from "@ng-bootstrap/ng-bootstrap";

import {AngularQuery}                   from "../../services/query/angularquery";
import {ItemComponent}                  from "../../articles/item/item.component";
import {College}                        from "../../services/college/college";
import {CollegeService}                 from "../../services/college/college.service";

import {_catch}                         from "rxjs/operator/catch";
import {_do}                            from "rxjs/operator/do";
import {debounceTime}                   from "rxjs/operator/debounceTime";
import {distinctUntilChanged}           from "rxjs/operator/distinctUntilChanged";
import {isNullOrUndefined}              from "util";
import {Observable}                     from "rxjs/Observable";
import {of}                             from "rxjs/observable/of";
import {Subscription}                   from "rxjs/Subscription";
import {switchMap}                      from "rxjs/operator/switchMap";


/**
 * A typeahead that accesses the 'college' table in the database.
 */
@Component({
	selector: 'college-typeahead',
	providers: [CollegeService],
	templateUrl: './college-typeahead.component.html',
})
export class CollegeTypeaheadComponent implements OnDestroy {

	public model:any;   // Lets the component talk to the hosting page.

	// These are semaphores used to hide or display messages.
	exactMatch = true;
	searchFailed = false;
	searching = false;
	collegeFound:Subscription;


	/**
	 * Initialize the component.
	 *
	 * @param {CollegeService} collegeService  The service that returns College objects.
	 */
	constructor(

		private collegeService: CollegeService

	) {}

	/**
	 * Unsubscribe to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.collegeFound) {

			this.collegeFound.unsubscribe();

		}

	}


	@Input()
	public hideAddButton:boolean = false;  // Should the 'Add' button be displayed.  This comes in from the parent page.


	@Input()
	public setRequired:boolean = false;  // Whether this is a required field or not.  This comes in from the parent page.


	@Input()
	public size:string;  // The size of the components.  This comes in from the parent page.


	@Input()
	public setTabIndex:number;  // The tab index for the host page.  This comes in from the parent page.


	@Input()
	public tag_id:number;  // The tag ID of the value from the host page.  This comes in from the parent page.


	/**
	 * Controls the college choices INPUT.
	 */
	@ViewChild('typeaheadCollegeInput')
	typeaheadCollegeInput: any;


	@Output()
	public onCollegeChange = new EventEmitter<College>();  // This notifies the parent page a College has been selected.


	/**
	 * Target for the 'Add' button.  This adds the selected tag to the tag list and fills in
	 * the fields on the parent page.
	 */
	addCollege():void {

		// Hide the failed search.
		this.searchFailed = false;

		// Create the JSON object for post after filtering for illegal characters.
		let json:object = {};
		json['name'] = ItemComponent.replaceWordCharacters(this.typeaheadCollegeInput.nativeElement.value);

		// Create the post query.
		let query:AngularQuery = new AngularQuery();
		query.useCacheBuster(false);
		query.useParams(false);
		query.setEndpoint( AngularQuery.ENDPOINT_COLLEGE, 'new' );

		// Unsubscribe from any existing query.
		if (this.collegeFound) {

			this.collegeFound.unsubscribe();

		}

		// Save the college and get back the updated data.
		this.collegeFound = this.collegeService.save(query.getUrl(), JSON.stringify(json)).subscribe(

			(response) => {

				let t:College = new College(response);
				this.onCollegeChange.emit(t);

			},

			() => {

				window.alert('Unable to save college');

			}

		);

	}


	/**
	 * Used by the typeahead to get the College's 'name' field
	 * so it can be placed into the typeahead INPUT field when
	 * the choice is made.
	 *
	 * @param result  The College object selected by the user.
	 */
	collegeFormatter = (result: College) => result.name;


	/**
	 * The typeahead calls this method when the user makes
	 * a selection.  This notifies the parent page with an
	 * event that passes the data.
	 *
	 * @param selection  The event that holds the choice made by the user.
	 */
	collegeSelectedEvent(selection: NgbTypeaheadSelectItemEvent): void {

		this.onCollegeChange.emit(selection.item);

	}

	isExactCollegeMatch(collegeName: string, collegeList: College[]): boolean{
		const collegeMatch = (element: College) => {return element.name === collegeName}

		if (collegeList.findIndex(collegeMatch) > -1){
			return true;
		}

		return false;
	}


	/**
	 * The typeahead puts the search term into the
	 * CollegeService and processes the results.  It also
	 * sets the semaphores to show/hide typeahead
	 * progress.
	 *
	 * @return The College Observable that will return data or errors.
	 */
	collegeSearch = (tag: Observable<College[]>) =>

		_do.call(

			switchMap.call(

				_do.call(
					distinctUntilChanged.call(
						debounceTime.call(tag, 300)),
					() => {

						this.searching = true;

					}
				),

				(search: string) =>
					_catch.call(
						_do.call(

							this.collegeService.search(search),
							(colleges: College[]) => {
								this.searchFailed = 0 === colleges.length;
								this.searching = false;
								this.exactMatch = this.isExactCollegeMatch(search, colleges);
							}),

						() => {

							// Report the search failure.
							this.searchFailed = true;
							this.searching = false;
							return of.call([]);

						})
			),

			() => this.searching = false

		);


	/**
	 * Based on the size variable passed into the component,
	 * return a class string appropriate to display the button.
	 *
	 * @return {string} The class string.
	 */
	setButtonSize():string {

		if (isNullOrUndefined(this.size)) {
			return 'btn btn-success';
		} else if (this.size.toLowerCase().startsWith('s')) {
			return 'btn btn-sm btn-success';
		} else if (this.size.toLowerCase().startsWith('l')) {
			return 'btn btn-lg btn-success';
		}

	}


	/**
	 * Read the size variable passed in from the hosting template
	 * and set the controls based on that.  Also read the length of
	 * the INPUT field and the 'required' field and add that information
	 * to the class, too.
	 *
	 * For the size, if size is null, '' is returned.  The other choices are
	 * 'sm' and 'lg' versions of 'form-control'.
	 *
	 * For the INPUT field length, return 'ng-invalid' if the field length
	 * is zero.
	 *
	 * @returns {string}  The appropriate Bootstrap value for the form type.
	 */
	setSize(): string {

		let attribute:string = '';

		// Handle the size stuff.
		if (isNullOrUndefined(this.size)) {
			attribute = '';
		} else if (this.size.toLowerCase().startsWith('s')) {
			attribute = 'form-control-sm';
		} else if (this.size.toLowerCase().startsWith('l')) {
			attribute = 'form-control-lg';
		}

		// Handle the INPUT invalid status.
		if (! this.setRequired) {
			return attribute;
		} else if (isNullOrUndefined(this.typeaheadCollegeInput.nativeElement.value) || 0 === this.typeaheadCollegeInput.nativeElement.value.length) {
			return attribute + ' ng-invalid'.trim();
		} else {
			return attribute;
		}

	}

}



// WEBPACK FOOTER //
// ./src/app/typeaheads/college/college-typeahead.component.ts